import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Resolve an asset/image path to an absolute URL. Leaves http(s) URLs and data
// URIs untouched; prefixes backend-served relative paths (e.g. /api/files/...)
// with the backend origin so images load correctly in every environment.
export function assetUrl(path) {
  if (!path) return path;
  if (/^(https?:)?\/\//.test(path) || path.startsWith("data:")) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000,
});

// Deduplicate identical in-flight GETs (several components request /categories, /products… on mount).
const inflight = new Map();
const keyOf = (cfg) => `${cfg.baseURL || ""}${cfg.url}?${JSON.stringify(cfg.params || {})}`;
const baseAdapter = axios.getAdapter(api.defaults.adapter);
api.defaults.adapter = (cfg) => {
  if ((cfg.method || "get").toLowerCase() !== "get") return baseAdapter(cfg);
  const k = keyOf(cfg);
  if (inflight.has(k)) return inflight.get(k).then((r) => ({ ...r, config: cfg }));
  const p = baseAdapter(cfg).finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
};

// One safety net for failed requests: a request that never reached the server (offline, timeout,
// proxy hiccup) must never surface as an "Uncaught runtime error" that blanks the page.
// Every promise the client hands out gets a handled branch; callers that .catch() still work.
const isNetworkFailure = (e) => e?.isAxiosError && !e.response;
let lastNetToast = 0;
const noteFailure = (e) => {
  if (isNetworkFailure(e)) {
    console.warn("[api] network failure:", e.config?.method, e.config?.url, e.code || e.message);
    if (typeof window !== "undefined" && Date.now() - lastNetToast > 15000) {
      lastNetToast = Date.now();
      import("sonner").then(({ toast }) => toast.error("Connection problem — some content couldn't load. Check your internet and try again.")).catch(() => {});
    }
  } else if (e?.response?.status !== 401) {
    console.warn("[api] request failed:", e?.config?.method, e?.config?.url, e?.response?.status);
  }
};
const origRequest = api.request.bind(api);
api.request = (...args) => {
  const p = origRequest(...args);
  p.catch(noteFailure);
  return p;
};

// Step-up reauthentication (#56): when the backend replies 403 "Reauthentication
// required", open the reauth modal, let the user confirm their password (+ 2FA),
// then transparently retry the original request once.
let _reauthHandler = null;
export function setReauthHandler(fn) { _reauthHandler = fn; }

api.interceptors.request.use((config) => {
  const m = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(m)) {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    if (match) config.headers["X-CSRF-Token"] = decodeURIComponent(match[1]);
    if (/^\/?(checkout|pay\/|payments?\/)/.test((config.url || "").replace(/^\//, "")) && !config.headers["Idempotency-Key"]) {
      config.headers["Idempotency-Key"] = (window.crypto?.randomUUID?.() || `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && detail === "Reauthentication required" && _reauthHandler && !cfg.__reauthRetried) {
      cfg.__reauthRetried = true;
      const ok = await _reauthHandler();
      if (ok) return api(cfg);
    }
    // Exactly one retry for idempotent GETs that failed before reaching the server (never loops).
    if (isNetworkFailure(error) && (cfg.method || "get").toLowerCase() === "get" && !cfg.__netRetried && typeof navigator !== "undefined" && navigator.onLine !== false) {
      cfg.__netRetried = true;
      await new Promise((res) => setTimeout(res, 800));
      return api(cfg);
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
